<template>
  <teleport to="body">
    <div v-if="open" class="image-cropper-overlay" @keydown.esc="cancel" tabindex="-1" ref="overlay">
      <div class="image-cropper-dialog shadow-lg">
        <div class="d-flex align-items-center justify-content-between px-3 py-2 border-bottom bg-light">
          <h6 class="m-0">
            <i class="bi bi-crop me-2"></i>{{ title }}
          </h6>
          <button type="button" class="btn-close" aria-label="Close" @click="cancel"></button>
        </div>
        <div class="p-3">
          <p class="small text-muted mb-2">
            <i class="bi bi-info-circle me-1"></i>
            {{ hint || 'Drag and zoom to fit the image in the frame.' }}
            <span v-if="sizeLabel" class="fw-semibold"> Output: {{ sizeLabel }}</span>
          </p>
          <div class="image-cropper-stage">
            <img ref="image" :src="objectUrl" alt="Image to crop" v-if="objectUrl" @load="initCropper">
          </div>
          <div class="d-flex flex-wrap gap-2 mt-3 justify-content-center">
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Zoom in" @click="cropper && cropper.zoom(0.1)">
              <i class="bi bi-zoom-in"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Zoom out" @click="cropper && cropper.zoom(-0.1)">
              <i class="bi bi-zoom-out"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Rotate left" @click="cropper && cropper.rotate(-90)">
              <i class="bi bi-arrow-counterclockwise"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Rotate right" @click="cropper && cropper.rotate(90)">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Flip horizontally" @click="flip">
              <i class="bi bi-symmetry-vertical"></i>
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" title="Reset" @click="cropper && cropper.reset()">
              <i class="bi bi-arrow-repeat"></i>
            </button>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 px-3 py-2 border-top bg-light">
          <button type="button" class="btn btn-outline-secondary" @click="cancel">Cancel</button>
          <button type="button" class="btn btn-primary" :disabled="!cropper" @click="apply">
            <i class="bi bi-check-lg me-1"></i>Crop &amp; use image
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

// Keep uploads below the API's 1MB limit
const MAX_OUTPUT_BYTES = 1000 * 1024

/**
 * Usage:
 *  <ImageCropper :open="cropOpen" :file="rawFile" :aspect-ratio="16/5" :output-width="1920"
 *                @cropped="dataUrl => ..." @closed="cropOpen = false" />
 */
export default {
  name: 'ImageCropper',

  emits: ['cropped', 'closed'],

  props: {
    open: { type: Boolean, default: false },
    file: { type: [File, Blob], default: null },
    aspectRatio: { type: Number, default: NaN },
    outputWidth: { type: Number, default: null },
    outputHeight: { type: Number, default: null },
    outputType: { type: String, default: 'image/jpeg' },
    quality: { type: Number, default: 0.88 },
    title: { type: String, default: 'Crop image' },
    hint: { type: String, default: '' },
  },

  data() {
    return {
      cropper: null,
      objectUrl: null,
      scaleX: 1,
    }
  },

  computed: {
    targetHeight() {
      if (this.outputHeight) return this.outputHeight
      if (this.outputWidth && this.aspectRatio) return Math.round(this.outputWidth / this.aspectRatio)
      return null
    },
    sizeLabel() {
      if (!this.outputWidth) return ''
      return `${this.outputWidth} × ${this.targetHeight}px`
    },
  },

  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (value) this.load()
        else this.destroy()
      },
    },
    file() {
      if (this.open) this.load()
    },
  },

  methods: {
    load() {
      this.destroy()
      if (!this.file) return
      this.objectUrl = URL.createObjectURL(this.file)
      this.$nextTick(() => this.$refs.overlay && this.$refs.overlay.focus())
    },
    initCropper() {
      if (this.cropper) this.cropper.destroy()
      this.scaleX = 1
      this.cropper = new Cropper(this.$refs.image, {
        aspectRatio: this.aspectRatio || NaN,
        viewMode: 1,
        autoCropArea: 1,
        dragMode: 'move',
        responsive: true,
      })
    },
    flip() {
      if (!this.cropper) return
      this.scaleX = -this.scaleX
      this.cropper.scaleX(this.scaleX)
    },
    apply() {
      if (!this.cropper) return
      const options = { imageSmoothingEnabled: true, imageSmoothingQuality: 'high' }
      if (this.outputWidth) options.width = this.outputWidth
      if (this.targetHeight) options.height = this.targetHeight
      if (this.outputType === 'image/jpeg') options.fillColor = '#ffffff'
      const canvas = this.cropper.getCroppedCanvas(options)
      if (!canvas) return
      this.$emit('cropped', this.toDataUrl(canvas))
      this.$emit('closed')
    },
    toDataUrl(canvas) {
      let quality = this.quality
      let dataUrl = canvas.toDataURL(this.outputType, quality)
      // PNG ignores quality: fall back to JPEG if a PNG would exceed the upload limit
      if (this.outputType === 'image/png' && this.byteSize(dataUrl) > MAX_OUTPUT_BYTES) {
        dataUrl = canvas.toDataURL('image/jpeg', quality)
      }
      while (this.byteSize(dataUrl) > MAX_OUTPUT_BYTES && quality > 0.4) {
        quality -= 0.1
        dataUrl = canvas.toDataURL('image/jpeg', quality)
      }
      return dataUrl
    },
    byteSize(dataUrl) {
      return Math.ceil((dataUrl.length - dataUrl.indexOf(',') - 1) * 3 / 4)
    },
    cancel() {
      this.$emit('closed')
    },
    destroy() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl)
        this.objectUrl = null
      }
    },
  },

  beforeUnmount() {
    this.destroy()
  },
}
</script>

<style scoped>
.image-cropper-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  outline: none;
}

.image-cropper-dialog {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 900px;
  max-height: 95vh;
  overflow: auto;
}

.image-cropper-stage {
  height: 60vh;
  max-height: 520px;
  background: #f1f3f5;
  border-radius: 6px;
  overflow: hidden;
}

.image-cropper-stage img {
  display: block;
  max-width: 100%;
}
</style>
